import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { Menu, X, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import MagneticButton from "../../common/MagneticButton";
import { PLAY_STORE_URL } from "../../../content/product";

// Order must mirror the order the sections appear on the page, so clicking
// down the nav walks the page top to bottom.
const NAV_LINKS = [
  { label: "How It Works", to: "#how-it-works" },
  { label: "Features", to: "#features" },
  { label: "About", to: "#about" },
  { label: "Contact", to: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the drawer if the viewport grows past the mobile breakpoint.
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = (e) => e.matches && setMenuOpen(false);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () =>
      mq.removeEventListener
        ? mq.removeEventListener("change", onChange)
        : mq.removeListener(onChange);
  }, []);

  return (
    <header
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9000,
        padding: scrolled ? "10px 0" : "18px 0",
        transition: "padding 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      <div className="container">
        <nav
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            height: "58px",
            padding: "0 12px 0 16px",
            borderRadius: "16px",
            background: scrolled ? "rgba(255,255,255,0.88)" : "rgba(255,255,255,0.6)",
            backdropFilter: "blur(14px) saturate(180%)",
            WebkitBackdropFilter: "blur(14px) saturate(180%)",
            border: `1px solid ${scrolled ? "rgba(226,232,240,0.9)" : "rgba(226,232,240,0.45)"}`,
            boxShadow: scrolled
              ? "0 1px 2px rgba(15,23,42,0.04), 0 10px 28px -18px rgba(15,23,42,0.35)"
              : "none",
            transition:
              "background 0.3s cubic-bezier(0.22,1,0.36,1), border-color 0.3s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <Link
            to="/"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              textDecoration: "none",
              flexShrink: 0,
            }}
          >
            <img
              src="assets/1024.png"
              alt=""
              aria-hidden="true"
              style={{ width: "28px", height: "28px", borderRadius: "8px" }}
            />
            <span
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: "1.06rem",
                color: "var(--text-main)",
                letterSpacing: "-0.024em",
              }}
            >
              EMIBOOK
            </span>
          </Link>

          <div
            className="d-none d-md-flex"
            style={{ alignItems: "center", gap: "30px" }}
          >
            {NAV_LINKS.map((link) => (
              <HashLink key={link.to} smooth to={link.to} className="eb-nav-link">
                {link.label}
              </HashLink>
            ))}
          </div>

          <div className="d-none d-md-flex" style={{ flexShrink: 0 }}>
            <MagneticButton>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="eb-btn eb-btn-primary eb-btn-sm"
              >
                Get Started <ArrowRight size={14} />
              </a>
            </MagneticButton>
          </div>

          <button
            className="d-flex d-md-none"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-main)",
              padding: "6px",
              cursor: "pointer",
              alignItems: "center",
            }}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
            style={{
              position: "fixed",
              top: "78px",
              left: "16px",
              right: "16px",
              background: "#ffffff",
              borderRadius: "16px",
              border: "1px solid var(--border-default)",
              padding: "10px 18px 18px",
              boxShadow: "0 20px 44px -22px rgba(15,23,42,0.4)",
              zIndex: 9001,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              {NAV_LINKS.map((link) => (
                <HashLink
                  key={link.to}
                  smooth
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    color: "var(--text-main)",
                    textDecoration: "none",
                    padding: "14px 0",
                    borderBottom: "1px solid var(--border-light)",
                  }}
                >
                  {link.label}
                </HashLink>
              ))}

              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="eb-btn eb-btn-primary"
                style={{ marginTop: "18px", width: "100%" }}
              >
                Get Started <ArrowRight size={15} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
